'use client';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @primitive Field
 * ROLE: none on Field (group-without-fieldset); inner control owns its native role.
 * ARIA: label-for/id via useId; aria-describedby (description then error);
 *       aria-invalid; aria-required. Prop mode and slot mode wire the same way:
 *       Field.Description / Field.Error register themselves with the Field.
 * KEYBOARD: native browser tab/typing.
 * FOCUS: when error appears via async validation, focus moves to the field's input
 *        (handled by parent <Form> wrapper via id lookup).
 * LIVE-REGION: Field.Error has role="alert" + aria-live="polite".
 * PRM: no internal motion.
 * FORCED-COLORS: standard.
 * RTL: logical properties for spacing.
 * RSC: CLIENT (state tracking for ids + context).
 * WAI-APG: Form Validation pattern.
 * SUBSTRATE: @radix-ui/react-label (FormLabel — internal).
 */
import { Children, isValidElement, useCallback, useId, useMemo, useState, } from 'react';
import { FormLabel } from '../../_internal/FormLabel/FormLabel';
import { FieldContext, hasMessageContent, } from './FieldContext';
import { FieldControl } from './FieldControl';
import { FieldDescription } from './FieldDescription';
import { FieldError } from './FieldError';
import { FieldLabel } from './FieldLabel';
import styles from './Field.module.css';
function FieldInner({ label, description, error, required = false, layout = 'stacked', children, style, }) {
    const baseId = useId();
    const ids = useMemo(() => ({ control: `${baseId}-field`, description: `${baseId}-desc`, error: `${baseId}-err` }), [baseId]);
    const [registered, setRegistered] = useState({ description: 0, error: 0 });
    const registerMessage = useCallback((slot) => {
        setRegistered((prev) => ({ ...prev, [slot]: prev[slot] + 1 }));
        return () => setRegistered((prev) => ({ ...prev, [slot]: prev[slot] - 1 }));
    }, []);
    const hasDescriptionProp = description != null;
    const hasErrorProp = hasMessageContent(error);
    // The props are known on the first render, so prop mode is wired before any
    // effect runs (and on the server); registration covers slot mode.
    const hasDescription = hasDescriptionProp || registered.description > 0;
    const invalid = hasErrorProp || registered.error > 0;
    const describedByParts = [
        hasDescription ? ids.description : undefined,
        invalid ? ids.error : undefined,
    ].filter((id) => id !== undefined);
    const describedBy = describedByParts.length > 0 ? describedByParts.join(' ') : undefined;
    const ctxValue = useMemo(() => ({ ids, describedBy, invalid, required, registerMessage }), [ids, describedBy, invalid, required, registerMessage]);
    // Prop mode: a single plain child is the control. Field.* compound members
    // wire themselves through context instead.
    let control = children;
    const childArray = Children.toArray(children);
    if (childArray.length === 1) {
        const only = childArray[0];
        if (isValidElement(only)) {
            const displayName = only.type.displayName;
            if (!displayName?.startsWith('Field.')) {
                control = _jsx(FieldControl, { children: only });
            }
        }
    }
    const labelEl = label != null ? (_jsx(FormLabel, { htmlFor: ids.control, required: required, children: label })) : null;
    const messages = (_jsxs(_Fragment, { children: [hasDescriptionProp ? _jsx(FieldDescription, { children: description }) : null, hasErrorProp ? _jsx(FieldError, { children: error }) : null] }));
    return (_jsx(FieldContext.Provider, { value: ctxValue, children: layout === 'inline' ? (_jsxs("div", { className: styles.inline, style: style, children: [_jsxs("div", { className: styles.inlineRow, children: [control, labelEl] }), messages] })) : (_jsxs("div", { className: styles.stacked, style: style, children: [labelEl, control, messages] })) }));
}
/**
 * Single-control field wrapper. Wires label, description, and error to the
 * inner control via id/aria attributes.
 *
 * Standard use (prop-driven):
 * ```tsx
 * <Field label="Email" description="We'll never share it." error={errors.email} required>
 *   <Input type="email" name="email" />
 * </Field>
 * ```
 *
 * Slot decomposition (10% case):
 * ```tsx
 * <Field>
 *   <Field.Label>Email <em>(required)</em></Field.Label>
 *   <Field.Control><Input type="email" /></Field.Control>
 *   <Field.Description>We'll never share it.</Field.Description>
 *   <Field.Error>{errors.email}</Field.Error>
 * </Field>
 * ```
 */
export const Field = Object.assign(FieldInner, {
    Label: Object.assign(FieldLabel, { displayName: 'Field.Label' }),
    Description: Object.assign(FieldDescription, { displayName: 'Field.Description' }),
    Error: Object.assign(FieldError, { displayName: 'Field.Error' }),
    Control: Object.assign(FieldControl, { displayName: 'Field.Control' }),
});
//# sourceMappingURL=Field.js.map